import React from "react";

const APPLIED_STYLE = {
  bg: "#ecfdf5",
  color: "#15803d",
  border: "transparent",
  label: "Applied",
};

const NOT_APPLIED_STYLE = {
  bg: "#f9fafb",
  color: "#374151",
  border: "#e1e7e4",
  label: "None",
};

function DiscountStatusIcon({ applied }: { applied: boolean }) {
  if (applied) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <circle cx="8" cy="8" r="8" fill="#22c55e" />
        <path
          d="M4.75 8.1L6.95 10.3L11.35 5.7"
          stroke="#ffffff"
          strokeWidth="1.45"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="8" fill="#9ca3af" />
      <path d="M5 8H11" stroke="#ffffff" strokeWidth="1.45" strokeLinecap="round" />
    </svg>
  );
}

type DiscountLabelBadgeProps = {
  applied: boolean;
  className?: string;
};

export default function DiscountLabelBadge({ applied, className }: DiscountLabelBadgeProps) {
  const style = applied ? APPLIED_STYLE : NOT_APPLIED_STYLE;

  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        padding: "5px 10px",
        borderRadius: "8px",
        fontSize: "0.8125rem",
        fontWeight: 500,
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        whiteSpace: "nowrap",
        lineHeight: 1.2,
      }}
    >
      <DiscountStatusIcon applied={applied} />
      {style.label}
    </span>
  );
}

/** True when a line has a coupon / order discount amount on the row. */
export function isLineDiscountApplied(amount: number | string | null | undefined): boolean {
  return Number(amount || 0) > 0;
}

/** Formatted per-line discount for product tables, e.g. `-$12.50` or `-` when none. */
export function formatLineDiscountAmount(amount: number | string | null | undefined): string {
  const n = Number(amount || 0);
  if (!Number.isFinite(n) || n <= 0) return "-";
  return `-$${n.toFixed(2)}`;
}

const roundMoney2 = (n: number) => Math.round(Number(n) * 100) / 100;

/** Agent/realtor checkout: table discount = (unit retail − changed) × qty; coupon only in order totals. */
export function getAgentRealtorCheckoutLinePricing(args: {
  item: any;
  qty: number;
  unitRetail: number;
  changedUnitPrice: number;
  perItemCouponMap?: Record<string, number>;
  lineKey: string;
  useLiveCoupon?: boolean;
  rowTax?: number;
}): {
  lineDiscount: number;
  lineTotal: number;
  couponDiscount: number;
  priceDiffDiscount: number;
} {
  const qty = Math.max(1, Number(args.qty || 1));
  const unitRetail = Number(args.unitRetail || 0);
  const changed = Number(args.changedUnitPrice || 0) || unitRetail;
  const rowTax = Number(args.rowTax || 0);
  const priceDiffDiscount = roundMoney2(Math.max(0, (unitRetail - changed) * qty));

  const live = args.perItemCouponMap?.[args.lineKey];
  const useLive = Boolean(args.useLiveCoupon && typeof live === "number");
  const savedCoupon = Number(
    args.item?.discount_on_total_quantity ??
      args.item?.discount_on_total_quantity_amount ??
      args.item?.discount ??
      0
  );
  const couponDiscount = useLive
    ? roundMoney2(Number(live))
    : savedCoupon > 0
      ? roundMoney2(savedCoupon)
      : 0;

  // Product-table Discount column: assignment / price-list diff only (not coupon).
  const lineDiscount = priceDiffDiscount;
  // Line total before order-level coupon; coupon is shown in order totals below.
  // const lineTotal = roundMoney2(changed * qty + rowTax);
  const lineTotal = roundMoney2(changed * qty);

  return { lineDiscount, lineTotal, couponDiscount, priceDiffDiscount };
}

/** Signage create/edit & sales order line table: discount = (actual − changed) × qty; total = changed × qty + tax. */
export function getSignageOrderLinePricing(args: {
  actualUnitPrice: number;
  changedUnitPrice: number;
  qty: number;
  tax?: number;
  /** When user manually edited unit price, retail = changed → no assignment discount line. */
  isManualUnitPrice?: boolean;
}): { rowDiscount: number; lineTotal: number } {
  const qty = Math.max(1, Number(args.qty || 1));
  const actual = Number(args.actualUnitPrice || 0);
  const changed = Number(args.changedUnitPrice || 0);
  const tax = Number(args.tax || 0);
  const rowDiscount = args.isManualUnitPrice
    ? 0
    : roundMoney2(Math.max(0, (actual - changed) * qty));
  // const lineTotal = roundMoney2(changed * qty + tax);
  const lineTotal = roundMoney2(changed * qty);
  return { rowDiscount, lineTotal };
}

type DiscountAmountDisplayProps = {
  amount: number | string | null | undefined;
  className?: string;
};

/** Shows applied discount amount; `-` when no discount (no badge). */
export function DiscountAmountDisplay({ amount, className }: DiscountAmountDisplayProps) {
  const applied = isLineDiscountApplied(amount);
  return (
    <span
      className={className}
      style={applied ? { color: "#00a63e" } : undefined}
    >
      {formatLineDiscountAmount(amount)}
    </span>
  );
}

/** Sum per-line product discounts on a saved installation order. */
export function sumInstallationLineItemDiscounts(lineItems?: any[] | null): number {
  return (lineItems || []).reduce(
    (s, li) =>
      s +
      Number(
        li?.discount ?? li?.discount_amount ?? li?.discount_on_total_quantity ?? 0
      ),
    0
  );
}

/** Infer product vs job scope when API omits discount_applies_to (common on saved orders). */
export function inferInstallationDiscountAppliesTo(
  order: { discount_applies_to?: string; product_discount_amount?: number; line_items?: any[] } | null | undefined,
  lineItemsDiscountTotal?: number
): string {
  const explicit = String(order?.discount_applies_to ?? "").toLowerCase();
  if (explicit === "product" || explicit === "installation" || explicit === "job") {
    return explicit === "job" ? "installation" : explicit;
  }
  const lineDisc =
    lineItemsDiscountTotal ??
    sumInstallationLineItemDiscounts(order?.line_items);
  if (lineDisc > 0 || Number(order?.product_discount_amount ?? 0) > 0) return "product";
  return "installation";
}

/** Show per-product discount badges after a product coupon is applied, or on saved lines in view. */
export function shouldShowProductDiscountLabels(opts: {
  discountCode?: string | null;
  productDiscountAmount?: number | null;
  /** When "installation", job coupon is active — hide product line badges unless lines have discount. */
  discountAppliesTo?: string | null;
  lineItemsDiscountTotal?: number | null;
}): boolean {
  const lineDisc = Number(opts.lineItemsDiscountTotal ?? 0);
  const scope = String(opts.discountAppliesTo ?? "").toLowerCase();
  if (scope === "installation" || scope === "job") {
    if (lineDisc <= 0) return false;
  }

  if (lineDisc > 0) return true;

  const code = String(opts.discountCode ?? "").trim();
  const amount = Number(opts.productDiscountAmount ?? 0);
  if (!code || amount <= 0) return false;
  const effectiveScope = scope || "product";
  return effectiveScope === "product";
}
